import type { ColorSpec } from './types';

/** Green color spec — uses the `mantis` Tailwind palette. */
export const green: ColorSpec = {
  filled: {
    border: 'border-mantis-700 dark:border-mantis-500',
    bg: 'bg-mantis-600 text-white dark:bg-mantis-700',
    bgActive: 'bg-mantis-800 text-white',
    hover: 'hover:bg-mantis-700 hover:text-white active:bg-mantis-800 active:text-white',
    hoverDark: 'dark:hover:bg-mantis-800 dark:hover:text-white dark:active:bg-mantis-900 dark:active:text-white',
  },
  outline: {
    border: 'border-mantis-400 dark:border-mantis-500',
    bg: 'bg-mantis-50 dark:bg-mantis-950 text-mantis-700 dark:text-mantis-200',
    bgActive: 'bg-mantis-600 dark:bg-mantis-500 text-white',
    hover: 'hover:bg-mantis-100 hover:text-mantis-800 active:bg-mantis-200 active:text-mantis-800',
    hoverDark:
      'dark:hover:bg-mantis-900 dark:hover:text-mantis-100 dark:active:bg-mantis-800 dark:active:text-mantis-100',
    hoverWhenActive: 'hover:bg-mantis-700 dark:hover:bg-mantis-600',
  },
  soft: {
    border: 'border-mantis-100 dark:border-mantis-900',
    bg: 'bg-mantis-50 dark:bg-mantis-950 text-mantis-700 dark:text-mantis-300 font-normal',
    bgActive: 'bg-mantis-600 dark:bg-mantis-500 text-white',
    hover: 'hover:bg-mantis-100 hover:text-mantis-900 active:bg-mantis-200 active:text-mantis-950',
    hoverDark:
      'dark:hover:bg-mantis-900 dark:hover:text-mantis-100 dark:active:bg-mantis-800 dark:active:text-mantis-100',
    hoverWhenActive: 'hover:bg-mantis-700 dark:hover:bg-mantis-600',
  },
  textFilled: {
    text: 'text-mantis-600 dark:text-mantis-400',
    activeBg: 'bg-mantis-600 text-white dark:bg-mantis-700',
    hover: 'hover:bg-mantis-600 hover:text-white active:bg-mantis-700',
    hoverDark: 'dark:hover:bg-mantis-700 dark:hover:text-white dark:active:bg-mantis-800',
  },
  textSoft: {
    text: 'text-mantis-700 dark:text-mantis-300 font-normal',
    activeBg: 'bg-mantis-600 text-white dark:bg-mantis-500',
    hover: 'hover:bg-mantis-100 hover:text-mantis-900 active:bg-mantis-200',
    hoverDark: 'dark:hover:bg-mantis-900 dark:hover:text-mantis-100 dark:active:bg-mantis-800',
  },
  tooltip: 'bg-mantis-600 dark:bg-mantis-700 text-white',
  badge: {
    filled: 'bg-mantis-600 dark:bg-mantis-700 text-white',
    soft: 'bg-mantis-400 dark:bg-mantis-500 text-white',
  },
  softIcon: 'text-mantis-700 dark:text-mantis-200',
  underline: {
    filled:
      '[&_u]:text-mantis-200 dark:[&_u]:text-mantis-300 [&_u]:decoration-mantis-300 dark:[&_u]:decoration-mantis-400',
    outline:
      '[&_u]:text-mantis-800 dark:[&_u]:text-mantis-300 [&_u]:decoration-mantis-600 dark:[&_u]:decoration-mantis-400',
    soft: '[&_u]:text-mantis-800 dark:[&_u]:text-mantis-300 [&_u]:decoration-mantis-300 dark:[&_u]:decoration-mantis-400',
  },
  input: {
    filled: {
      border: 'border-mantis-700 dark:border-mantis-500',
      bg: 'bg-mantis-600 dark:bg-mantis-700',
      focusBg: 'focus-within:bg-mantis-700 dark:focus-within:bg-mantis-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-mantis-700 dark:focus-within:outline-mantis-500',
      hoverActive:
        'hover:bg-mantis-700 hover:text-white active:bg-mantis-800 active:text-white dark:hover:bg-mantis-800 dark:active:bg-mantis-900',
      readOnlyBg: 'bg-mantis-500 dark:bg-mantis-800',
      text: 'text-white',
    },
    outline: {
      border: 'border-mantis-400 dark:border-mantis-500',
      bg: 'bg-mantis-50 dark:bg-mantis-950',
      focusBg: 'focus-within:bg-mantis-100 dark:focus-within:bg-mantis-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-mantis-400 dark:focus-within:outline-mantis-500',
      hoverActive:
        'hover:bg-mantis-100 hover:text-mantis-800 active:bg-mantis-200 active:text-mantis-800 dark:hover:bg-mantis-900 dark:hover:text-mantis-100 dark:active:bg-mantis-800 dark:active:text-mantis-100',
      readOnlyBg: 'bg-mantis-50 dark:bg-mantis-900',
      text: 'text-mantis-700 dark:text-mantis-200',
    },
    soft: {
      border: 'border-mantis-100 dark:border-mantis-900',
      bg: 'bg-mantis-50 dark:bg-mantis-950',
      focusBg: 'focus-within:bg-mantis-100 dark:focus-within:bg-mantis-900',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-mantis-100 dark:focus-within:outline-mantis-900',
      hoverActive:
        'hover:bg-mantis-100 hover:text-mantis-900 active:bg-mantis-200 active:text-mantis-950 dark:hover:bg-mantis-900 dark:hover:text-mantis-100 dark:active:bg-mantis-800 dark:active:text-mantis-100',
      readOnlyBg: 'bg-mantis-50 dark:bg-mantis-900',
      text: 'text-mantis-700 dark:text-mantis-300',
    },
  },
  inputMuted: {
    filled: 'text-white',
    outline: 'text-mantis-300 dark:text-mantis-600',
    soft: 'text-mantis-300 dark:text-mantis-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-white',
    outline: 'placeholder:text-mantis-300 dark:placeholder:text-mantis-600',
    soft: 'placeholder:text-mantis-300 dark:placeholder:text-mantis-600',
  },
  group: {
    filled: 'border-mantis-300 dark:border-mantis-700 bg-mantis-50 dark:bg-mantis-950',
    outline: 'border-mantis-400 dark:border-mantis-500 bg-mantis-50 dark:bg-mantis-950',
    soft: 'border-mantis-200 dark:border-mantis-800 bg-mantis-100 dark:bg-mantis-900',
  },
  groupDivider: {
    filled: 'bg-mantis-300 dark:bg-mantis-700',
    outline: 'bg-mantis-300 dark:bg-mantis-600',
    soft: 'bg-mantis-200 dark:bg-mantis-700',
  },
  segment: {
    filled: 'border-mantis-300 dark:border-mantis-700 bg-mantis-50 dark:bg-mantis-950',
    outline: 'border-mantis-400 dark:border-mantis-500 bg-mantis-50 dark:bg-mantis-950',
  },
  segmentDivider: {
    filled: 'bg-mantis-300 dark:bg-mantis-700',
    outline: 'bg-mantis-300 dark:bg-mantis-600',
  },
  popoverHighlight: 'bg-mantis-600 text-white',
  popoverText: 'text-mantis-700 dark:text-mantis-200',
  popoverBorder: 'border-mantis-200 dark:border-mantis-700',
};
